import { onUnmounted, watchEffect } from 'vue';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';

// Intercom must never break the PLM. The messenger is a non-essential third-party
// widget, so every call into its SDK is wrapped: any failure is swallowed and
// logged, and the app keeps working without the messenger. Calls stay synchronous
// so shutdown() still clears cookies before a brand-switch/logout reload.
function safe<T extends (...args: never[]) => void>(fn: T, label: string) {
  return (...args: Parameters<T>): void => {
    try {
      fn(...args);
    } catch (e) {
      console.warn(`Intercom ${label} failed`, e);
    }
  };
}

export const intercomBoot = safe(Intercom, 'boot');
export const intercomShutdown = safe(shutdown, 'shutdown');

// The SDK types `update` as requiring args, but calling it argless just refreshes
// the session — which is all we do (on each route change).
export function intercomUpdate(): void {
  try {
    (update as () => void)();
  } catch (e) {
    console.warn('Intercom update failed', e);
  }
}

/**
 * Hides or restores the launcher bubble.
 *
 * The bubble is fixed to the bottom-right at a z-index nothing in the app can outrank, so a
 * panel that slides out from the right lands underneath it — over the agent composer's send
 * button, in particular. Rather than fight the stacking order, the panels ask for it to step
 * aside while they are open.
 *
 * Reference-counted: several panels can be open at once (a flyout over the chat), and the
 * bubble should come back when the last of them closes, not the first.
 */
let hiddenBy = 0;

export function intercomHideLauncher(hidden: boolean): void {
  const before = hiddenBy;
  hiddenBy = Math.max(0, hiddenBy + (hidden ? 1 : -1));
  if (before > 0 === hiddenBy > 0) return;

  try {
    (update as (settings: Record<string, unknown>) => void)({ hide_default_launcher: hiddenBy > 0 });
  } catch (e) {
    console.warn('Intercom launcher toggle failed', e);
  }
}

/**
 * Keeps the launcher out of the way for as long as `isOpen` is true, and restores it on
 * unmount — so a panel torn down while open does not leave the bubble hidden for good.
 */
export function useIntercomOutOfTheWay(isOpen: () => boolean): void {
  let hidden = false;
  const apply = (wanted: boolean) => {
    if (wanted === hidden) return;
    hidden = wanted;
    intercomHideLauncher(wanted);
  };

  watchEffect(() => apply(isOpen()));
  onUnmounted(() => apply(false));
}
